import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Home from './components/Home';
import EligibilityCheck from './components/EligibilityCheck';
import Footer from './components/Footer';
import Login from './components/Login';
import Signup from './components/Signup';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import StudentDetail from './components/admin/StudentDetail';
import StudentDashboard from './components/dashboard/StudentDashboard';
import BasicDetailsForm from './components/dashboard/BasicDetailsForm';
import AccountSettings from './components/dashboard/AccountSettings';
// import AIAssistant from './components/AIAssistant'; // Disabled
// import { MessageSquareText } from 'lucide-react'; // Disabled

// Policy Pages
import PrivacyPolicy from './components/policies/PrivacyPolicy';
import TermsConditions from './components/policies/TermsConditions';
import CookiePolicy from './components/policies/CookiePolicy';
import RefundPolicy from './components/policies/RefundPolicy';
import DataRetention from './components/policies/DataRetention';
import ComplaintsPolicy from './components/policies/ComplaintsPolicy';
// Admissions Pages
import CourseMatching from './components/admissions/CourseMatching';
import AdmissionsEngine from './components/admissions/AdmissionsEngine';
import ApplicationTracker from './components/admissions/ApplicationTracker';
import ScholarshipFinder from './components/admissions/ScholarshipFinder';
import DestinationAnalytics from './components/admissions/DestinationAnalytics';
import AboutPage from './components/AboutPage';

// English Prep Hub Pages
import EnglishDiagnostics from './components/english-prep-lab/EnglishDiagnostics';
import VoiceCoach from './components/english-prep-lab/VoiceCoach';
import StudyPaths from './components/english-prep-lab/StudyPaths';

// Old Coaching Pages (kept for potential fallback or redirects)
import AcademicPreparation from './components/coaching/AcademicPreparation';
import LanguageSupport from './components/coaching/LanguageSupport';
import StudentReadinessProgram from './components/coaching/StudentReadinessProgram';
import PreArrivalCoaching from './components/coaching/PreArrivalCoaching';
import UnderConstruction from './components/UnderConstruction';

// Immigration Pages
import VisaProbabilityScoring from './components/immigration/VisaProbabilityScoring';
import ComplianceEngine from './components/immigration/ComplianceEngine';
import MilestoneTracker from './components/immigration/MilestoneTracker';
import ImmigrationSolicitors from './components/immigration/ImmigrationSolicitors';
import StudentVisaGuidance from './components/immigration/StudentVisaGuidance';
import ApplicationDocumentReview from './components/immigration/ApplicationDocumentReview';
import VisaProcessSupport from './components/immigration/VisaProcessSupport';
import ConnectWithSolicitors from './components/immigration/ConnectWithSolicitors';

// Transport Pages
import FlightBookingAssistance from './components/transport/FlightBookingAssistance';
import AirportPickup from './components/transport/AirportPickup';
import LocalTransportGuidance from './components/transport/LocalTransportGuidance';
import VehicleAssistance from './components/transport/VehicleAssistance';

// Accommodation Pages
import SafetyLocation from './components/accommodation/SafetyLocation';
import AccommodationGuidance from './components/accommodation/AccommodationGuidance';
import TemporaryHousing from './components/accommodation/TemporaryHousing';
import LongTermRental from './components/accommodation/LongTermRental';
import NotFound from './components/NotFound';

const App: React.FC = () => {
  // Chat disabled
  /*
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Auto-open chat after a delay for engagement
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only open if user hasn't interacted with it yet
      setIsChatOpen(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);
  */

  return (
    <HelmetProvider>
      <BrowserRouter>
        <div className="min-h-screen flex flex-col bg-white font-sans">
          <Header />

          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<Home />} />

              {/* Admissions Routes */}
              <Route path="/admissions/course-matching" element={<CourseMatching />} />
              <Route path="/admissions/admissions-engine" element={<AdmissionsEngine />} />
              <Route path="/admissions/application-tracker" element={<ApplicationTracker />} />
              <Route path="/admissions/scholarship-finder" element={<ScholarshipFinder />} />
              <Route path="/admissions/destination-analytics" element={<DestinationAnalytics />} />
              <Route path="/about" element={<AboutPage />} />

              <Route path="/eligibility" element={<EligibilityCheck />} />

              {/* English Prep Hub Routes */}
              <Route path="/english-prep-lab/diagnostics" element={<EnglishDiagnostics />} />
              <Route path="/english-prep-lab/voice-coach" element={<VoiceCoach />} />
              <Route path="/english-prep-lab/study-paths" element={<StudyPaths />} />

              {/* Legacy Coaching Routes (Redirects or keeping for now) */}
              <Route path="/coaching/academic-preparation" element={<AcademicPreparation />} />
              <Route path="/coaching/language-support" element={<LanguageSupport />} />
              <Route path="/coaching/student-readiness" element={<StudentReadinessProgram />} />
              <Route path="/coaching/pre-arrival" element={<PreArrivalCoaching />} />

              {/* Immigration Routes */}
              <Route path="/immigration/probability-scoring" element={<VisaProbabilityScoring />} />
              <Route path="/immigration/compliance-engine" element={<ComplianceEngine />} />
              <Route path="/immigration/milestone-tracker" element={<MilestoneTracker />} />
              <Route path="/immigration/solicitors" element={<ImmigrationSolicitors />} />
              <Route path="/immigration/student-visa-guidance" element={<StudentVisaGuidance />} />
              <Route path="/immigration/document-review" element={<ApplicationDocumentReview />} />
              <Route path="/immigration/process-support" element={<VisaProcessSupport />} />
              <Route path="/immigration/solicitors-legacy" element={<ConnectWithSolicitors />} />

              {/* Transport Routes */}
              <Route path="/transport/flight-booking" element={<FlightBookingAssistance />} />
              <Route path="/transport/airport-pickup" element={<AirportPickup />} />
              <Route path="/transport/local-transport" element={<LocalTransportGuidance />} />
              <Route path="/transport/vehicle-assistance" element={<VehicleAssistance />} />

              {/* Accommodation Routes */}
              <Route path="/accommodation/safety-location" element={<SafetyLocation />} />
              <Route path="/accommodation/guidance" element={<AccommodationGuidance />} />
              <Route path="/accommodation/temporary-housing" element={<TemporaryHousing />} />
              <Route path="/accommodation/long-term-rental" element={<LongTermRental />} />

              <Route path="/under-construction" element={<UnderConstruction />} />

              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/student/:id" element={<StudentDetail />} />

              {/* Student Dashboard Routes */}
              <Route path="/student/setup" element={<BasicDetailsForm />} />
              <Route path="/student/dashboard" element={<StudentDashboard />} />
              <Route path="/student/profile" element={<StudentDashboard initialTab="profile" />} />
              <Route path="/settings" element={<AccountSettings />} />

              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />

              {/* Policy Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/data-retention" element={<DataRetention />} />
              <Route path="/complaints-policy" element={<ComplaintsPolicy />} />

              {/* 404 Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>

          <Footer />

          {/* Chat Bot Interface - Disabled */}
          {/* <AIAssistant isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} /> */}

          {/* Floating Chat Button (when chat is closed) - Disabled */}
          {/* {!isChatOpen && (
          <button
            onClick={() => setIsChatOpen(true)}
            className="fixed bottom-6 right-6 z-50 bg-[#009B8F] hover:bg-[#00857a] text-white p-4 shadow-2xl transition-all transform hover:scale-105"
          >
            <MessageSquareText size={28} />
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-200 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
            </span>
          </button>
        )} */}
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;
